<template>
  <el-container class="favorite-detail-page">
    <div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
      <i class="el-icon-back back-btn" @click="onBack"></i>
      <span>{{ $t('favorite.detailTitle') }}</span>
    </div>
    <div class="detail-content">
      <div class="card">
        <div v-if="favorite.messageType === 0" class="text-card">
          {{ favorite.messageContent }}
        </div>
        <div v-else-if="favorite.messageType === 1" class="image-card">
          <img :src="imageUrl" class="detail-image" @click="onPreviewImage" />
        </div>
        <div v-else-if="favorite.messageType === 2" class="file-card">
          <div class="file-info-box">
            <i class="el-icon-document file-icon"></i>
            <div class="file-detail">
              <div class="file-name">{{ fileName }}</div>
              <div class="file-size">{{ fileSizeText }}</div>
            </div>
          </div>
          <el-button type="primary" size="small" @click="onPreviewFile">{{ $t('chat.download') }}</el-button>
        </div>
        <div v-else-if="favorite.messageType === 4" class="video-card">
          <video v-if="videoUrl" :src="videoUrl" controls class="detail-video" :poster="videoCoverUrl"></video>
        </div>
        <div class="time-text">{{ $t('favorite.collectedAt') }}{{ favorite.createdTime }}</div>
      </div>
      <div class="bottom-bar">
        <el-button type="primary" icon="el-icon-position" @click="onSendToFriend">{{ $t('favorite.sendToFriend') }}</el-button>
        <el-button type="danger" icon="el-icon-delete" @click="onDelete">{{ $t('common.delete') }}</el-button>
      </div>
    </div>
    <chat-selector ref="chatSel" :title="$t('favorite.sendToFriend')"></chat-selector>
  </el-container>
</template>

<script>
import ChatSelector from '../components/chat/ChatSelector.vue';

export default {
  name: "FavoriteDetailPage",
  components: {
    ChatSelector
  },
  data() {
    return {
      favorite: {}
    }
  },
  methods: {
    loadFavorite(id) {
      this.$http({
        url: '/favorite/info/' + id,
        method: 'GET'
      }).then((data) => {
        this.favorite = data;
      });
    },
    onBack() {
      this.$router.back();
    },
    onPreviewImage() {
      const url = this.imageUrl;
      if (url) {
        this.$eventBus.$emit('openFullImage', url);
      }
    },
    onPreviewFile() {
      const url = this.fileUrl;
      if (url) {
        window.open(url);
      }
    },
    onSendToFriend() {
      const fav = this.favorite;
      const msgType = fav.messageType === 0 ? this.$enums.MESSAGE_TYPE.TEXT : fav.messageType;
      this.$refs.chatSel.open(chats => {
        let idx = 0;
        chats.forEach(chat => {
          let msgInfo = {
            content: fav.messageContent,
            type: msgType
          };
          if (chat.type == 'PRIVATE') {
            msgInfo.recvId = chat.targetId;
          } else {
            msgInfo.groupId = chat.targetId;
          }
          let action = `/message/${chat.type.toLowerCase()}/send`;
          this.$http({
            url: action,
            method: 'POST',
            data: msgInfo
          }).then(m => {
            m.selfSend = true;
            this.chatStore.openChat(chat);
            this.chatStore.insertMessage(m, chat);
            if (++idx == chats.length) {
              this.$message.success(this.$t('favorite.sendSuccess'));
            }
          });
        });
      });
    },
    onDelete() {
      this.$confirm(this.$t('favorite.confirmDeleteContent'), this.$t('favorite.confirmDelete'), {
        confirmButtonText: this.$t('common.ok'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        this.$http({
          url: '/favorite/cancel/' + this.favorite.id,
          method: 'DELETE'
        }).then(() => {
          this.$message.success(this.$t('favorite.deleteSuccess'));
          this.$router.back();
        });
      });
    }
  },
  computed: {
    contentData() {
      if (this.favorite.messageContent) {
        try {
          return JSON.parse(this.favorite.messageContent);
        } catch (e) {
          return {};
        }
      }
      return {};
    },
    imageUrl() {
      if (this.favorite.messageType === 1 && this.favorite.messageContent) {
        try {
          const data = JSON.parse(this.favorite.messageContent);
          return data.originUrl || data.thumbUrl || '';
        } catch (e) {
          return this.favorite.messageContent;
        }
      }
      return '';
    },
    fileName() {
      return this.contentData.name || '';
    },
    fileUrl() {
      return this.contentData.url || '';
    },
    fileSizeText() {
      const size = this.contentData.size;
      if (!size) return '';
      if (size > 1024 * 1024) {
        return Math.round(size / 1024 / 1024) + 'M';
      }
      if (size > 1024) {
        return Math.round(size / 1024) + 'KB';
      }
      return size + 'B';
    },
    videoCoverUrl() {
      return this.contentData.coverUrl || '';
    },
    videoUrl() {
      return this.contentData.videoUrl || '';
    }
  },
  mounted() {
    const id = this.$route.query.id;
    if (id) {
      this.loadFavorite(id);
    }
  }
}
</script>

<style lang="scss" scoped>
.favorite-detail-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;

  .header {
    height: 60px;
    display: flex;
    align-items: center;
    padding: 0 24px;
    font-size: 18px;
    font-weight: 600;
    color: var(--im-text-color-primary);
    background: white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
    gap: 12px;

    .back-btn {
      cursor: pointer;
      padding: 8px;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        background: var(--im-background-active);
      }
    }
  }

  .detail-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .card {
      width: 100%;
      max-width: 700px;
      background: white;
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 24px;

      .text-card {
        background: var(--im-background-active);
        padding: 20px;
        border-radius: 12px;
        font-size: 15px;
        color: var(--im-text-color);
        line-height: 1.7;
        word-break: break-all;
        white-space: pre-wrap;
      }

      .image-card {
        .detail-image {
          width: 100%;
          border-radius: 12px;
          cursor: pointer;
        }
      }

      .file-card {
        background: var(--im-background-active);
        border-radius: 12px;
        padding: 20px;

        .file-info-box {
          display: flex;
          align-items: center;
          margin-bottom: 16px;

          .file-icon {
            font-size: 48px;
            color: #d42e07;
            margin-right: 16px;
          }

          .file-detail {
            flex: 1;

            .file-name {
              font-size: 15px;
              color: var(--im-text-color);
              font-weight: 600;
              word-break: break-all;
            }

            .file-size {
              font-size: 13px;
              color: var(--im-text-color-light);
              margin-top: 6px;
            }
          }
        }
      }

      .video-card {
        .detail-video {
          width: 100%;
          border-radius: 12px;
          max-height: 500px;
        }
      }

      .time-text {
        margin-top: 16px;
        font-size: 12px;
        color: var(--im-text-color-light);
        text-align: right;
      }
    }

    .bottom-bar {
      width: 100%;
      max-width: 700px;
      display: flex;
      gap: 12px;

      .el-button {
        flex: 1;
        height: 44px;
        border-radius: 10px;
        font-weight: 500;
      }
    }
  }
}
</style>
